import { useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import {
  Breadcrumb,
  Button,
  Input,
  Label,
  PageContainer,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { LoadMore } from "../../components/LoadMore";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

const LIBELLES_TYPE = {
  ajout: "Ajout",
  retrait: "Retrait",
  debit: "Évaluation créée",
} as const;

export function CreditsOrganisation() {
  const { id } = useParams<{ id: string }>();
  const organisationId = id as Id<"organisations">;

  const org = useQuery(api.superAdmin.obtenirOrganisation, { organisationId });
  const { results, status, loadMore } = usePaginatedQuery(
    api.superAdmin.listerMouvementsCredits,
    { organisationId },
    { initialNumItems: 20 },
  );
  const ajuster = useMutation(api.superAdmin.ajusterCredits);

  const [type, setType] = useState<"ajout" | "retrait">("ajout");
  const [montant, setMontant] = useState("");
  const [motif, setMotif] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const montantNombre = Number(montant);
    if (!Number.isInteger(montantNombre) || montantNombre <= 0) {
      setError("Le montant doit être un entier strictement positif.");
      return;
    }
    setSaving(true);
    try {
      await ajuster({ organisationId, type, montant: montantNombre, motif });
      setMontant("");
      setMotif("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  if (org === undefined) {
    return (
      <div className="flex justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (org === null) {
    return <div className="p-8 text-center text-muted-foreground">Organisation introuvable</div>;
  }

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(organisationId, org.nom, "credits")} />
      <PageHeader
        titre={libelleSection("credits")}
        sousTitre={
          <>
            Solde actuel :{" "}
            <strong className={org.credits < 0 ? "text-destructive" : "text-foreground"}>
              {org.credits}
            </strong>
          </>
        }
      />

      <form
        onSubmit={handleSubmit}
        className="bg-card border border-border rounded-surface p-5 mb-6 grid gap-4 sm:grid-cols-[10rem_8rem_1fr_auto] sm:items-end"
      >
        <div>
          <Label htmlFor="credits-type" className="mb-1">Opération</Label>
          <Select value={type} onValueChange={(v) => setType(v as "ajout" | "retrait")}>
            <SelectTrigger id="credits-type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ajout">Ajouter</SelectItem>
              <SelectItem value="retrait">Retirer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="credits-montant" className="mb-1">Montant</Label>
          <Input
            id="credits-montant"
            type="number"
            min={1}
            step={1}
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="credits-motif" className="mb-1">Motif (facultatif)</Label>
          <Input
            id="credits-motif"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Achat pack 100, geste commercial…"
          />
        </div>
        <Button type="submit" disabled={saving}>
          {saving ? "Enregistrement…" : "Valider"}
        </Button>
        {error && <p className="text-sm text-destructive sm:col-span-4">{error}</p>}
      </form>

      <div className="bg-card border border-border rounded-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent/5 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Montant</th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">Solde</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Motif</th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">Auteur</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {results.map((m) => (
              <tr key={m._id}>
                <td className="px-4 py-3 whitespace-nowrap">
                  {new Date(m._creationTime).toLocaleString("fr-FR")}
                </td>
                <td className="px-4 py-3">{LIBELLES_TYPE[m.type]}</td>
                <td
                  className={`px-4 py-3 text-right tabular-nums ${m.montant < 0 ? "text-destructive" : "text-green-700"}`}
                >
                  {m.montant > 0 ? `+${m.montant}` : m.montant}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{m.soldeApres}</td>
                <td className="px-4 py-3">{m.motif ?? "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{m.auteurEmail ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {status !== "LoadingFirstPage" && results.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            Aucun mouvement enregistré.
          </div>
        )}
      </div>
      <LoadMore status={status} loadMore={loadMore} count={results.length} />
    </PageContainer>
  );
}
